/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's opyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import { createLocalZustand } from "@/shared/state";

import { v1 as uuid } from "uuid";

import { countActiveFilters } from "../pages/manage-properties-filters";

type FetchSettings = { pageSize: number; page?: number; filters?: Filter[] };
type Filter = { column: string; operator: FilterOperator; compareTo: string };
type FilterOperator = "gt" | "lt" | "gte" | "lte" | "e" | "ne";

export type ListMode = 'grid' | 'table';

const LIST_MODE_STORAGE_KEY = 'property-list-mode';

/**
 * Reads the persisted list-view mode from localStorage, defaulting to `'table'`
 * when unset or when storage is unavailable (SSR / private browsing).
 *
 * @returns The stored `ListMode`, or `'table'` as fallback.
 */
function getPersistedListMode(): ListMode {
  try {
    const stored = localStorage.getItem(LIST_MODE_STORAGE_KEY);
    if (stored === 'grid' || stored === 'table') return stored;
  } catch { /* SSR or private browsing */ }
  return 'table';
}

export type PropertyFilters = {
  /**
   * Narrows the listing to the signed-in broker's own starred listings.
   * Which ids those are is resolved on the server from the token; the
   * query only carries the flag.
   */
  favorites?: boolean;
  /**
   * Listing status, as the API's own vocabulary spells it: active,
   * inactive, sold, rented, suspended. The overview's vacancy tile is what
   * writes it -- a vacant unit is one offered for rent and still active.
   */
  status?: string;
  /** ISO day; listings registered on or after it. Written by the overview. */
  created_after?: string;
  /** True narrows to the listings that cannot be published as they stand. */
  publication_blocked?: boolean;
  /** Ordering. "stock_days" puts the longest-standing listings first. */
  sort?: string;
  /**
   * Narrows the listing to one spreadsheet import, for "Ver só estes" on the
   * post-import strip. Server-side: which listings an import produced is not
   * knowable from a property record alone.
   */
  import_id?: string;
  /** Listings nobody has visited in at least this many days. */
  days_since_visit_gte?: number;
  state?: string;
  city?: string;
  neighborhood?: string;
  category?: string[];
  transaction_type?: string[];
  price_gte?: number;
  price_lte?: number;
  rental_price_gte?: number;
  rental_price_lte?: number;
  bedrooms?: number;
  bathrooms?: number;
  parking_spots?: number;
  suites?: number;
  constructed_area_gte?: number;
  constructed_area_lte?: number;
  total_area_gte?: number;
  total_area_lte?: number;
};

export type CrudContext = {
  fetchId: string;
  refreshFetch: () => void;

  fetchSettings: FetchSettings;
  setFetchSettings: (fetchSettings: FetchSettings) => void;
  rows: any[];

  isLoading: boolean;
  setIsLoading: (isLoading: boolean) => void;

  isTableVisible: boolean;
  setIsTableVisible: (value: boolean) => void;
  toggleIsTableExpanded: () => void;

  isFormVisible: boolean;
  setIsFormVisible: (value: boolean) => void;

  editingEntity: Record<string, any> | null;
  setEditingEntity: (entity: Record<string, any> | null) => void;

  tableStatistics: Record<string, any> | null;
  setStatistics: (statistics: Record<string, any> | null) => void;

  totalPages: number;
  setTotalPages: (n: number) => void;

  listMode: ListMode;
  setListMode: (mode: ListMode) => void;

  filtersOpen: boolean;
  setFiltersOpen: (open: boolean) => void;

  advancedFilters: PropertyFilters;
  setAdvancedFilters: (filters: PropertyFilters) => void;
  clearAdvancedFilters: () => void;
  activeFilterCount: number;
};

const EMPTY_FILTERS: PropertyFilters = {};

export const {
  Consumer: CrudConsumer,
  Context: CrudContext,
  Provider: CrudProvider,
  useStore: useCrudStore,
} = createLocalZustand<CrudContext>((initializer) => (set, get) => ({
  fetchId: uuid(),
  refreshFetch: () => set(() => ({ fetchId: uuid() })),

  rows: [],

  isLoading: true,
  setIsLoading: (isLoading) => set(() => ({ isLoading })),

  isTableVisible: true,
  setIsTableVisible: (value) => set(() => ({ isTableVisible: value })),
  toggleIsTableExpanded: () =>
    set((state) => ({ isTableVisible: !state.isTableVisible })),

  isFormVisible: false,
  setIsFormVisible: (value) => set(() => ({ isFormVisible: value })),

  fetchSettings: {
    pageSize: initializer?.fetchSettings?.pageSize ?? 10,
    page: initializer?.fetchSettings?.page ?? 1,
  },
  setFetchSettings: (fetchSettings) => set(() => ({ fetchSettings })),

  editingEntity: null,
  setEditingEntity: (entity) => set(() => ({ editingEntity: entity })),

  tableStatistics: null,
  setStatistics: (statistics) => set(() => ({ tableStatistics: statistics })),

  totalPages: 1,
  setTotalPages: (n) => set(() => ({ totalPages: n })),

  listMode: getPersistedListMode(),
  setListMode: (mode) => {
    try { localStorage.setItem(LIST_MODE_STORAGE_KEY, mode); } catch { /* noop */ }
    set(() => ({ listMode: mode }));
  },

  filtersOpen: false,
  setFiltersOpen: (open) => set(() => ({ filtersOpen: open })),

  advancedFilters: EMPTY_FILTERS,
  setAdvancedFilters: (filters) =>
    set(() => ({ advancedFilters: filters, activeFilterCount: countActiveFilters(filters) })),
  clearAdvancedFilters: () =>
    set(() => ({ advancedFilters: EMPTY_FILTERS, activeFilterCount: 0 })),
  activeFilterCount: 0,
}));
